// One shared Socket.IO connection for live updates across the whole app - see
// server/realtime/liveUpdates.js for what the server sends and why.
//
// Screens don't talk to the socket directly:
//  - useLiveSync (mounted once in App) keeps shared Redux data (items,
//    categories, units, customers, suppliers, staff, ...) current;
//  - any screen that loads its own data (lists, details, dashboard) calls
//    useLiveData(["invoices", ...], reload) to re-read when it changes.
import { io } from "socket.io-client";
import { API_BASE } from "../config";

const readToken = () => {
  try {
    return JSON.parse(localStorage.getItem("profile") || "null")?.token || null;
  } catch (error) {
    return null;
  }
};

// Same place the REST API lives: in production API_BASE is `<origin>/api`
// (IIS strips /api before proxying to Node), so the socket goes to
// `<origin>/api/socket.io`; in dev it's http://localhost:5000/socket.io.
const resolveEndpoint = () => {
  const url = new URL(API_BASE, window.location.origin);
  return {
    origin: url.origin,
    path: `${url.pathname.replace(/\/+$/, "")}/socket.io`,
  };
};

const listeners = new Set();
const statusListeners = new Set();
let socket = null;
// "off" (logged out / not started), "connecting", "live", "reconnecting".
let status = "off";

const setStatus = (next) => {
  if (next === status) return;
  status = next;
  statusListeners.forEach((listener) => listener(status));
};

export const getLiveStatus = () => status;
export const subscribeLiveStatus = (listener) => {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
};

// When the last live update arrived - background re-reads started right
// after one are live refreshes, not something the user is waiting for (the
// top progress bar ignores them, see components/Loader/TopProgressBar.js).
let lastLiveEventAt = 0;
export const isWithinLiveRefresh = (windowMs = 3000) => Date.now() - lastLiveEventAt < windowMs;
let connectedToken = null;
let retryTimer = null;

// event: "changed" (payload = { [topic]: { ids, deleted, all } }) or
// "resync" (payload = { reason }) - re-read everything shown.
const notify = (event, payload) => {
  lastLiveEventAt = Date.now();
  listeners.forEach((listener) => {
    try {
      listener(event, payload);
    } catch (error) {
      console.error("Live update listener failed:", error);
    }
  });
};

// True while the live connection is up - timer-based safety-net refreshes
// (CompanyContext, PermissionsContext) skip their poll then, since every
// change already arrives here instantly.
export const isLiveConnected = () => !!socket?.connected;

export const subscribeLive = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const disconnectLiveSocket = () => {
  clearTimeout(retryTimer);
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  connectedToken = null;
  setStatus("off");
};

// Idempotent: call it whenever the session may have changed (navigation,
// login, logout, token renewal). Connects, reconnects with a new token, or
// disconnects when logged out.
export const syncLiveSocket = () => {
  const token = readToken();
  if (!token) {
    disconnectLiveSocket();
    return;
  }
  if (socket && connectedToken === token) return;

  disconnectLiveSocket();
  connectedToken = token;
  const { origin, path } = resolveEndpoint();

  socket = io(origin, {
    path,
    // Read on every (re)connect so a renewed token is picked up automatically.
    auth: (cb) => cb({ token: readToken() }),
    // Starts on long-polling and upgrades to WebSocket when IIS allows it, so
    // it works even where the IIS WebSocket feature isn't installed.
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  setStatus("connecting");
  let hasConnectedBefore = false;
  socket.on("connect", () => {
    setStatus("live");
    // Anything that changed while we were disconnected was never sent -
    // re-read everything once on every RE-connect (not the first connect,
    // when the screens are loading fresh data anyway).
    if (hasConnectedBefore) notify("resync", { reason: "reconnected" });
    hasConnectedBefore = true;
  });
  socket.on("data:changed", (message) => notify("changed", message?.topics || {}));
  socket.on("data:resync", (message) => notify("resync", message || {}));
  socket.on("connect_error", (error) => {
    // An auth rejection is final for this socket (Socket.IO won't retry it).
    // Try again later in case the token was just being renewed.
    setStatus(hasConnectedBefore ? "reconnecting" : "connecting");
    if (error?.message === "unauthorized") {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        connectedToken = null;
        syncLiveSocket();
      }, 30000);
    }
  });
  socket.io.on("reconnect_attempt", () => setStatus("reconnecting"));
  socket.on("disconnect", (reason) => {
    setStatus("reconnecting");
    // "io server disconnect" = server dropped us (token expired): reconnect
    // with whatever token is current now.
    if (reason === "io server disconnect") {
      connectedToken = null;
      setTimeout(syncLiveSocket, 1000);
    }
  });
};
